
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { ApiResponseData, BaseService, FaqService, ICountry, ICurrency, IFaq, IPlatform, SanitizedResponse } from "../../../../../services";
import { NavLink, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button } from "@rivtn/noseur";

export interface FaqsProps {
    platform: IPlatform;
}

function Faqs(props: FaqsProps) {
    const faqService = FaqService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [faqs, setFaqs] = React.useState<ApiResponseData<IFaq>>({});
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.faqs;
    labels.sided_labels.dropdown_form![0].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    return SidedContainer({
        data: faqs,
        searchParams,
        setSearchParams,
        onAction: manageFaq,
        queryData: fetchFaqs,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (faq: IFaq) => {
            return (<div className="hera-item" onClick={() => {
                manageFaq(faq);
                Utils.updateSearchParams("adi", faq.external_id, setSearchParams);
            }}>
                <div>
                    <div className="title lined-limit">{faq.title} <span> - {i18nManager.Languages.find((l) => l.value == faq.language)?.label} ({faq.language})</span></div>
                    <div className="content lined-limit">{faq.content}</div>
                </div>
            </div>);
        },
    } as any);

    function fetchFaqs() {
        faqService.queryFaqs(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<IFaq>) => {
            setFaqs(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageFaq(faq: IFaq = {} as any) {
        const isUpdate = !!faq.title;
        const dialogLabels = labels.dialogs.add_edit;
        faq.language = faq.language ?? searchParams.get("language");

        const dialog = ConfirmDialog({
            closable: true,
            title: (isUpdate ? dialogLabels.edit_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_action_label : dialogLabels.add_action_label),
            style: { minWidth: 500, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: any, searching: any) => {
                    dialogErrorRef.current!.innerText = " ";
                    (faq as any)[name] = value;
                    return false;
                }, faq, undefined, { marginTop: 0 }, {
                    "language": i18nManager.Languages
                })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            footerElements: (isUpdate ? (<Button scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 30px" }} onClick={() => {
                dialog.hide(() => deleteFaq(faq));
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(faq).length < 3) {
                    dialogErrorRef.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                faq.platform_id = props.platform.platform_id;
                const method = (isUpdate ? faqService.updateFaq : faqService.createFaq).bind(faqService);
                method(faq).then(({ }: SanitizedResponse<IFaq>) => {
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.destroy(fetchFaqs);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function deleteFaq(faq: IFaq) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${title}", faq.title)
                .replace("${language}", faq.language),
            onCancel: () => {
                dialog.hide(() => manageFaq(faq));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                faqService.deleteFaq(faq.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchFaqs);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Faqs;
